/**
 * i18n.js
 *
 * Resolves a recruiter's stored locale to a translation file in
 * `./translations` and loads that file as its own webpack chunk.
 *
 *   IMPORTANT: This file is used by the internal build
 *   script `i18n:update`, and must use CommonJS module syntax
 *   You CANNOT use import/export in this file.
 */

require('./i18n.polyfills');

const enTranslationMessages = require('./translations/en.json');

// ********************************************************************************
// == Constant ====================================================================
const DEFAULT_LOCALE = 'en';

const defaultTranslationMessages = formatTranslationMessages(
  DEFAULT_LOCALE,
  enTranslationMessages,
);

/**
 * Locales whose stored code is not the name of a file in `./translations`.
 *
 * The hire app stores `LanguageUtils::Locales.profile_locale_code`, which
 * prefers a two-letter short code; Smartling names each export after the long
 * BCP 47 code. A locale absent from this map resolves by exact file name, so a
 * new Smartling export needs an entry only when the two conventions disagree.
 *
 * `en-US` and `en-GB` resolve to `en` because the recruiter Smartling job has
 * no English regional target, both render the source strings.
 */
const LOCALE_FILE_OVERRIDES = {
  bg: 'bg-BG',
  cs: 'cs-CZ',
  de: 'de-DE',
  el: 'el-GR',
  'en-GB': 'en',
  'en-US': 'en',
  es: 'es-ES',
  fi: 'fi-FI',
  fr: 'fr-FR',
  gu: 'gu-IN',
  'hi-in': 'hi-IN',
  ht: 'ht-HT',
  id: 'id-ID',
  it: 'it-IT',
  ja: 'ja-JP',
  kn: 'kn-IN',
  ko: 'ko-KR',
  ml: 'ml-IN',
  mr: 'mr-IN',
  nl: 'nl-NL',
  no: 'nb-NO',
  pa: 'pa-IN',
  pl: 'pl-PL',
  pt: 'pt-BR',
  ro: 'ro-RO',
  ru: 'ru-RU',
  so: 'so-SO',
  sv: 'sv-SE',
  ta: 'ta-IN',
  te: 'te-IN',
  th: 'th-TH',
  tr: 'tr-TR',
  tw: 'zh-TW',
  vi: 'vi-VN',
  zh: 'zh-CN',
};

const messagesByFile = { [DEFAULT_LOCALE]: defaultTranslationMessages };

// In-flight loads, so the provider and the pre-paint hold in AuthRoute share one
// request and one format pass instead of doing the work twice on every cold load.
const loadsByFile = {};

// == Util ========================================================================
function formatTranslationMessages(locale, messages) {
  const defaultFormattedMessages =
    locale !== DEFAULT_LOCALE
      ? formatTranslationMessages(DEFAULT_LOCALE, enTranslationMessages)
      : {};
  const flattenFormattedMessages = (formattedMessages, key) => {
    const formattedMessage =
      !messages[key] && locale !== DEFAULT_LOCALE
        ? defaultFormattedMessages[key]
        : messages[key];
    return Object.assign(formattedMessages, { [key]: formattedMessage });
  };
  return Object.keys(messages).reduce(flattenFormattedMessages, {});
}

/**
 * Fetches one catalog. The template literal makes webpack emit one chunk per
 * file in `./translations`, so a session downloads only the language it renders.
 *
 * Only the fetch is guarded. Formatting runs outside the `try` so a malformed
 * catalog or a bug in `formatTranslationMessages` surfaces instead of being
 * swallowed on the same path as a missing chunk.
 */
async function fetchTranslationFile(file) {
  let loaded;

  try {
    loaded = await import(
      /* webpackChunkName: "translations/[request]" */ `./translations/${file}.json`
    );
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error(`#8f2c41d7 Failed to load translations for ${file}`, error);
    return null;
  }

  return formatTranslationMessages(file, loaded.default || loaded);
}

/**
 * Loads the messages for `locale`, falling back to the source strings when the
 * locale has no translation file or its chunk fails to download.
 *
 * A failure is not cached, so a later attempt retries rather than pinning the
 * session to English. A success is, so a locale is fetched and formatted once.
 */
async function loadTranslationMessages(locale) {
  const file = resolveTranslationFile(locale);
  if (messagesByFile[file]) return messagesByFile[file];

  if (!loadsByFile[file]) {
    loadsByFile[file] = fetchTranslationFile(file).finally(() => {
      delete loadsByFile[file];
    });
  }

  const messages = await loadsByFile[file];
  if (!messages) return defaultTranslationMessages;

  messagesByFile[file] = messages;
  return messages;
}

/**
 * The messages already in memory for `locale`, or `undefined` while its chunk
 * has yet to load. Synchronous, so a caller can decide whether it has to wait
 * before rendering — see `useLocaleReady`.
 */
function peekTranslationMessages(locale) {
  return messagesByFile[resolveTranslationFile(locale)];
}

/** Name of the translation file that serves `locale`. */
function resolveTranslationFile(locale) {
  return LOCALE_FILE_OVERRIDES[locale] || locale || DEFAULT_LOCALE;
}

// == Export ======================================================================
exports.DEFAULT_LOCALE = DEFAULT_LOCALE;
exports.defaultTranslationMessages = defaultTranslationMessages;
exports.formatTranslationMessages = formatTranslationMessages;
exports.loadTranslationMessages = loadTranslationMessages;
exports.LOCALE_FILE_OVERRIDES = LOCALE_FILE_OVERRIDES;
exports.peekTranslationMessages = peekTranslationMessages;
exports.resolveTranslationFile = resolveTranslationFile;
